<script setup lang="ts">
/**
 * FeatureCollectionRenderer
 *
 * Unified component for rendering GeoJSON FeatureCollections on Mapbox maps.
 * Replaces both MapProjectLayer and LayerCollectedData with a single, configurable renderer.
 *
 * Features:
 * - Renders polygons, lines, and points
 * - Clustering support with configurable settings
 * - Image markers (rounded square style)
 * - Grid preview clusters (2-4 photos)
 * - Age-based styling (optional)
 * - Layer manager integration for z-layer ordering
 * - Unified popup via FeaturePopup component
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { useGeoStore } from '@/stores/geo'
import type { Map as MapboxMap, MapMouseEvent, GeoJSONSource } from 'mapbox-gl'
import { MapSource, MapLayer } from '@/components/map'
import ImageClusterRenderer from './ImageClusterRenderer.vue'
import HeavyFeatureMarker from './HeavyFeatureMarker.vue'
import { FeaturePopup } from '@/components/feature'
import { useLayerManager } from '@/composables/useLayerManager'
import {
  useMetricPaint,
  DEFAULT_COLOR,
  DEFAULT_FILL_OPACITY
} from '@/composables/useMetricPaint'
import { useSelectionPaint } from '@/composables/useAreaSelection'
import { createLayerId, createSourceId } from '@/components/layers/types'
import {
  loadImage,
  processImageToRoundedSquare,
  IMAGE_CONFIG
} from '@/components/map/utils/imageProcessor'
import { centroid } from '@turf/turf'

interface Props {
  map: MapboxMap
  features: GeoJSON.FeatureCollection
  groupId: string

  // Optional configuration
  enableClustering?: boolean
  clusterMaxZoom?: number
  clusterMinPoints?: number
  enableImageMarkers?: boolean
  enableAgeBasedStyling?: boolean

  // For layer ordering
  beforeId?: string
  position?: number
  isVisible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  enableClustering: true,
  clusterMaxZoom: 17,
  clusterMinPoints: 2,
  enableImageMarkers: true,
  enableAgeBasedStyling: false,
  isVisible: true
})

const emit = defineEmits<{
  (e: 'feature-click', feature: GeoJSON.Feature, event: MapMouseEvent): void
  (e: 'cluster-click', cluster: GeoJSON.Feature, event: MapMouseEvent): void
  (e: 'popup-open', feature: GeoJSON.Feature): void
  (e: 'popup-close'): void
}>()

const userStore = useUserStore()

const geoStore = useGeoStore()
const layerManager = useLayerManager(computed(() => props.map))
const settings = computed(() => userStore.settings || { tz: 'UTC' })

// Source IDs
const mixedSourceId = computed(() => createSourceId(props.groupId, 'mixed'))
const pointsSourceId = computed(() => createSourceId(props.groupId, 'points'))
const shapeCentresSourceId = computed(() => createSourceId(props.groupId, 'shape-centres'))

// Layer IDs
const fillLayerId = computed(() => createLayerId(props.groupId, 'fill'))
const lineLayerId = computed(() => createLayerId(props.groupId, 'line'))
const pointLayerId = computed(() => createLayerId(props.groupId, 'point'))
const shapeCentreLayerId = computed(() => createLayerId(props.groupId, 'shape-centre'))

// Polygons and lines shrink below a pixel when zoomed out, so their centres
// cluster and show as dots up to this zoom; past it the shapes speak for themselves.
const SHAPE_CENTRES_MAX_ZOOM = 11

// Computed beforeId based on position or prop
const computedBeforeId = computed(() => {
  if (props.beforeId) return props.beforeId
  if (props.position !== undefined) return `z-${props.position}`
  return undefined
})

// Non-null only while the Monitoring page has an expanded metric for this group.
const metricPaint = useMetricPaint(computed(() => props.groupId))
// Non-null while a monitoring area is selected for this group. The metric
// choropleth wins on color; selection then only thickens the selected outline.
const selectionPaint = useSelectionPaint(computed(() => props.groupId))

const fillOptions = computed(() => ({
  filter: ['==', '$type', 'Polygon'],
  paint: {
    'fill-color': metricPaint.value?.fillColor ?? DEFAULT_COLOR,
    'fill-opacity':
      metricPaint.value?.fillOpacity ?? selectionPaint.value?.fillOpacity ?? DEFAULT_FILL_OPACITY
  },
  layout: {
    visibility: props.isVisible ? 'visible' : 'none'
  }
}))

const lineOptions = computed(() => ({
  paint: {
    'line-color': metricPaint.value?.lineColor ?? selectionPaint.value?.lineColor ?? DEFAULT_COLOR,
    'line-width': selectionPaint.value?.lineWidth ?? 2,
    'line-opacity': 0.9
  },
  layout: {
    visibility: props.isVisible ? 'visible' : 'none'
  }
}))

const imageMarkerLayerId = computed(() => createLayerId(props.groupId, 'image-marker'))

const imageMarkerOptions = computed(() => ({
  filter: ['all', ['!has', 'point_count'], ['has', 'thumbUrl']],
  layout: {
    'icon-image': ['get', 'thumbUrl'],
    'icon-size': 1,
    'icon-allow-overlap': true,
    'icon-anchor': 'center',
    visibility: props.isVisible ? 'visible' : 'none'
  }
}))

const circleOptions = computed(() => ({
  filter: props.enableImageMarkers
    ? ['all', ['!has', 'point_count'], ['!has', 'thumbUrl']]
    : ['!has', 'point_count'],
  paint: {
    'circle-color': ['coalesce', ['get', 'color'], '#0059ff'],
    'circle-radius': 8,
    'circle-stroke-width': 2,
    'circle-stroke-color': '#ffffff'
  },
  layout: {
    visibility: props.isVisible ? 'visible' : 'none'
  }
}))

const shapeCentreSize = (size: number) => ['step', ['zoom'], size, SHAPE_CENTRES_MAX_ZOOM + 1, 0]

const shapeCentreOptions = computed(() => ({
  filter: ['!has', 'point_count'],
  paint: {
    'circle-color': ['coalesce', ['get', 'color'], DEFAULT_COLOR],
    'circle-radius': shapeCentreSize(6),
    'circle-stroke-width': shapeCentreSize(2),
    'circle-stroke-color': '#ffffff'
  },
  layout: {
    visibility: props.isVisible ? 'visible' : 'none'
  }
}))

// Loaded heavy geometries from geo store cache
const loadedHeavyGeometries = computed(() => geoStore.loadedHeavyGeometries)

// Enrich features by substituting loaded heavy geometries
const enrichedFeatures = computed<GeoJSON.Feature[]>(() => {
  if (!props.features?.features) return []

  const cache = loadedHeavyGeometries.value
  return props.features.features.map((f) => {
    const code = f.properties?.code
    if (!f.geometry && f.properties?.is_heavy && code && cache[code]) {
      return { ...f, geometry: cache[code] }
    }
    return f
  })
})

// Split features by geometry type
const mixedFeatures = computed<GeoJSON.FeatureCollection>(() => {
  const nonPoints = enrichedFeatures.value.filter(
    (f) => f.geometry && f.geometry.type !== 'Point'
  )

  // Apply age calculation if enabled
  if (props.enableAgeBasedStyling) {
    const now = Date.now()
    return {
      type: 'FeatureCollection',
      features: nonPoints.map((f) => ({
        ...f,
        properties: {
          ...f.properties,
          ageInDays: calculateAgeInDays(f.properties?.created_at, now)
        }
      }))
    }
  }

  return { type: 'FeatureCollection', features: nonPoints }
})

const pointFeatures = computed<GeoJSON.FeatureCollection>(() => {
  const points = enrichedFeatures.value.filter(
    (f) => f.geometry?.type === 'Point'
  )

  // Apply age calculation if enabled
  if (props.enableAgeBasedStyling) {
    const now = Date.now()
    return {
      type: 'FeatureCollection',
      features: points.map((f) => ({
        ...f,
        properties: {
          ...f.properties,
          ageInDays: calculateAgeInDays(f.properties?.created_at, now)
        }
      }))
    }
  }

  return { type: 'FeatureCollection', features: points }
})

const shapeCentres = computed<GeoJSON.FeatureCollection>(() => ({
  type: 'FeatureCollection',
  features: mixedFeatures.value.features
    .map((f) => ({ ...centroid(f), properties: f.properties }))
    .filter((c) => c.geometry.coordinates.every(Number.isFinite))
}))

const shapeCentresClusterOptions = computed(() => {
  if (!props.enableClustering) return {}

  return {
    cluster: true,
    clusterRadius: 80,
    clusterMaxZoom: SHAPE_CENTRES_MAX_ZOOM,
    clusterMinPoints: props.clusterMinPoints,
  }
})

// Heavy features without geometry that need placeholder markers
const heavyPlaceholderFeatures = computed<GeoJSON.Feature[]>(() => {
  return enrichedFeatures.value.filter(
    (f) =>
      !f.geometry &&
      f.properties?.is_heavy &&
      f.properties?.center_lat != null &&
      f.properties?.center_lng != null &&
      f.properties?.code
  )
})

function handleLoadGeometry(code: string) {
  geoStore.loadHeavyFeatureGeometry(code)
}

function isFeatureLoading(code: string): boolean {
  return geoStore.isHeavyFeatureLoading(code)
}

function isFeatureLoaded(code: string): boolean {
  return geoStore.isHeavyFeatureLoaded(code)
}

const clusterOptions = computed(() => {
  if (!props.enableClustering) return {}

  return {
    cluster: true,
    clusterRadius: 80,
    clusterMaxZoom: props.clusterMaxZoom,
    clusterMinPoints: props.clusterMinPoints,
  }
})

// Popup state
const showPopup = ref(false)
const popupFeature = ref<GeoJSON.Feature | null>(null)
const popupCoordinates = ref<[number, number] | null>(null)
const popupCounter = ref(0)

/**
 * Calculate age in days from created_at timestamp
 */
function calculateAgeInDays(createdAt: string | undefined, now: number): number {
  if (!createdAt) return 30 // Default age

  const createdAtTime = new Date(createdAt).getTime()
  return Math.max(0, (now - createdAtTime) / (1000 * 60 * 60 * 24))
}

/**
 * Handle feature click
 */
function handleFeatureClick(feature: GeoJSON.Feature, event: MapMouseEvent): void {
  emit('feature-click', feature, event)

  // Show popup
  openPopup(feature, event)
}

/**
 * Handle cluster click
 */
function handleClusterClick(cluster: GeoJSON.Feature, event?: MapMouseEvent | MouseEvent): void {
  emit('cluster-click', cluster, event as MapMouseEvent)
}

/**
 * Open popup for a feature
 */
function openPopup(feature: GeoJSON.Feature, event?: MapMouseEvent): void {
  // Close existing popup first
  if (showPopup.value) {
    closePopup()
    // Small delay before opening new one
    setTimeout(() => {
      popupFeature.value = feature
      popupCoordinates.value = getFeatureCoordinates(feature, event)
      showPopup.value = true
      popupCounter.value++
      emit('popup-open', feature)
    }, 50)
  } else {
    popupFeature.value = feature
    popupCoordinates.value = getFeatureCoordinates(feature, event)
    showPopup.value = true
    popupCounter.value++
    emit('popup-open', feature)
  }
}

/**
 * Close popup
 */
function closePopup(): void {
  showPopup.value = false
  popupFeature.value = null
  popupCoordinates.value = null
  emit('popup-close')
}

/**
 * Get coordinates for popup positioning
 */
function getFeatureCoordinates(feature: GeoJSON.Feature, event?: MapMouseEvent): [number, number] {
  if (event?.lngLat) {
    return event.lngLat.toArray() as [number, number]
  }

  const geometry = feature.geometry
  if (geometry.type === 'Point') {
    return geometry.coordinates as [number, number]
  }

  // For non-point geometries, calculate the centroid
  const center = centroid(feature)
  return center.geometry.coordinates as [number, number]
}

/**
 * Handle click on fill/line layers
 */
function handleLayerClick(e: MapMouseEvent): void {
  const features = e.features
  if (!features || features.length === 0) return

  handleFeatureClick(features[0], e)
}

/**
 * A dot stands in for its shape: hand on the shape itself, unless the shape
 * under the cursor already took this click.
 */
function handleShapeCentreClick(e: MapMouseEvent): void {
  const code = e.features?.[0]?.properties?.code
  const shape = mixedFeatures.value.features.find((f) => f.properties?.code === code)
  if (!shape) return
  const shapeHit = props.map.queryRenderedFeatures(e.point, { layers: [fillLayerId.value, lineLayerId.value] })
    .some((f) => f.properties?.code === code)
  if (!shapeHit) handleFeatureClick(shape, e)
}

/**
 * Handle click on heavy feature placeholder
 */
function handleHeavyFeatureClick(feature: GeoJSON.Feature): void {
  const coords: [number, number] = [
    feature.properties?.center_lng,
    feature.properties?.center_lat
  ]
  popupFeature.value = feature
  popupCoordinates.value = coords
  showPopup.value = true
  popupCounter.value++
  emit('popup-open', feature)
}

/**
 * Load and add a single image to the map (for styleimagemissing handler)
 */
async function loadAndAddImage(imageUrl: string): Promise<void> {
  if (props.map.hasImage(imageUrl)) return

  try {
    const img = await loadImage(imageUrl)
    const processed = processImageToRoundedSquare(img)
    // Check again before adding (race condition protection)
    if (!props.map.hasImage(imageUrl)) {
      props.map.addImage(imageUrl, processed, { pixelRatio: IMAGE_CONFIG.singlePixelRatio })
    }
  } catch (error) {
    // Ignore errors in production - image may have been added by another handler
    if (import.meta.env.DEV) {
      console.warn('[FeatureCollectionRenderer] Failed to load image:', imageUrl, error)
    }
  }
}

/**
 * Handle missing image requests from Mapbox
 */
function handleStyleImageMissing(e: { id: string }): void {
  // Only handle URLs (thumbUrl values)
  if (e.id && (e.id.startsWith('http://') || e.id.startsWith('https://'))) {
    loadAndAddImage(e.id)
  }
}

// Layer manager integration
onMounted(() => {
  if (props.position !== undefined) {
    layerManager.registerGroup(props.groupId, props.position)
  }

  // Register layers
  layerManager.registerLayer(props.groupId, fillLayerId.value)
  layerManager.registerLayer(props.groupId, lineLayerId.value)
  layerManager.registerLayer(props.groupId, pointLayerId.value)
  layerManager.registerLayer(props.groupId, shapeCentreLayerId.value)

  // Listen for missing images (thumbUrl) and load them
  props.map.on('styleimagemissing', handleStyleImageMissing)
})

onUnmounted(() => {
  // Remove event listener
  props.map.off('styleimagemissing', handleStyleImageMissing)

  // Unregister layers
  layerManager.unregisterLayer(props.groupId, shapeCentreLayerId.value)
  layerManager.unregisterLayer(props.groupId, pointLayerId.value)
  layerManager.unregisterLayer(props.groupId, lineLayerId.value)
  layerManager.unregisterLayer(props.groupId, fillLayerId.value)

  if (props.position !== undefined) {
    layerManager.unregisterGroup(props.groupId)
  }
})

// Watch for position changes
watch(() => props.position, (newPosition, oldPosition) => {
  if (newPosition !== oldPosition && newPosition !== undefined) {
    layerManager.moveGroupToPosition(props.groupId, newPosition)
  }
})

defineExpose({ openPopup })
</script>

<template>
  <div>
    <!-- Source for non-point geometries (polygons, lines).
         Always mounted: fill/line are stable anchors other layers insert
         against, and an empty collection renders nothing anyway. -->
    <MapSource
      :map="map"
      type="geojson"
      :id="mixedSourceId"
      :data="mixedFeatures"
    >
    <!-- Polygon fills -->
    <MapLayer
      :map="map"
      :id="fillLayerId"
      :source="mixedSourceId"
      type="fill"
      :before-id="computedBeforeId"
      :group-id="groupId"
      :options="fillOptions"
      @click="handleLayerClick"
    />

    <!-- Lines and polygon outlines, above the fill so overlays can sit between -->
    <MapLayer
      :map="map"
      :id="lineLayerId"
      :source="mixedSourceId"
      type="line"
      :before-id="computedBeforeId"
      :group-id="groupId"
      :options="lineOptions"
      @click="handleLayerClick"
    />
    </MapSource>

    <!-- Centres of polygons and lines, clustered and dotted while zoomed out -->
    <MapSource
      v-if="shapeCentres.features.length > 0"
      :map="map"
      type="geojson"
      :id="shapeCentresSourceId"
      :data="shapeCentres"
      :options="shapeCentresClusterOptions"
    >
    <ImageClusterRenderer
      v-if="enableClustering"
      :map="map"
      :source-id="shapeCentresSourceId"
      :group-id="groupId"
      :is-visible="isVisible"
      @cluster-click="handleClusterClick"
    />

    <MapLayer
      :map="map"
      :id="shapeCentreLayerId"
      :source="shapeCentresSourceId"
      type="circle"
      :before-id="computedBeforeId"
      :group-id="groupId"
      :options="shapeCentreOptions"
      @click="handleShapeCentreClick"
    />
    </MapSource>

    <!-- Source for points with clustering -->
    <MapSource
      v-if="pointFeatures.features.length > 0"
      :map="map"
      type="geojson"
      :id="pointsSourceId"
      :data="pointFeatures"
      :options="clusterOptions"
    >
    <!-- Image cluster renderer -->
    <ImageClusterRenderer
      v-if="enableClustering"
      :map="map"
      :source-id="pointsSourceId"
      :group-id="groupId"
      :is-visible="isVisible"
      @cluster-click="handleClusterClick"
    />

    <!-- Single image markers (unclustered points with thumbUrl) -->
    <MapLayer
      v-if="enableImageMarkers"
      :map="map"
      :id="imageMarkerLayerId"
      :source="pointsSourceId"
      type="symbol"
      :group-id="groupId"
      :options="imageMarkerOptions"
      @click="handleLayerClick"
    />

    <!-- Circle markers (unclustered points without thumbUrl) -->
    <MapLayer
      :map="map"
      :id="pointLayerId"
      :source="pointsSourceId"
      type="circle"
      :before-id="computedBeforeId"
      :group-id="groupId"
      :options="circleOptions"
      @click="handleLayerClick"
    />
    </MapSource>

    <!-- Feature Popup -->
    <FeaturePopup
      v-if="showPopup && popupFeature && popupCoordinates"
      :map="map"
      :is-visible="showPopup"
      :coordinates="popupCoordinates"
      :feature="popupFeature"
      :settings="settings"
      :code="groupId"
      :counter="popupCounter"
      @close="closePopup"
    />

    <!-- Heavy feature placeholders -->
    <HeavyFeatureMarker
      v-for="feature in heavyPlaceholderFeatures"
      :key="feature.properties?.code"
      :map="map"
      :feature="feature"
      :is-visible="isVisible"
      :is-loading="isFeatureLoading(feature.properties?.code)"
      :is-loaded="isFeatureLoaded(feature.properties?.code)"
      :on-marker-click="handleHeavyFeatureClick"
      :on-load-geometry="handleLoadGeometry"
    />
  </div>
</template>
